import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { JSX, useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { getUserRowType } from "../../schema/console/user";
import { PageContext } from "./Pages";
import StyledButton from "../../components/StyledButton";
import { GroupBase } from "../../schema/generated/Group";
import { Action, Func, VoidAction } from "../../util/func";
import useInput, { useInputType } from "../../util/useInput";
import { OnChangeTextField } from "../../components/TextField";

export default function UserPageLayout({ pageContext, userData, groupData, update }: { pageContext: PageContext, userData: getUserRowType, groupData: GroupBase[], update: VoidAction } ) {
  return (
    <Routes>
      <Route index element={<>ユーザーIDが見つかりません。</>} />
      <Route path=":id/" element={<UserPage pageContext={pageContext} userData={userData} groups={groupData} update={update} />}></Route>
      <Route path="new/" element={<UserCreatePage pageContext={pageContext} groups={groupData} update={update} />} />
    </Routes>
  );
}

function UserCreatePage({ pageContext, groups, update }: { pageContext: PageContext, groups: GroupBase[], update: VoidAction }) {
  const navigate = useNavigate();

  // 初期値は空文字にする(undefined 始まりだと React が非制御→制御の切替警告を出す)
  const [group_id, setGroupId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [loginId, setLoginId] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [secondPassword, setSecondPassword] = useState<string>("");
  // authorityは入力UIが無いため、新規作成時は既定値0で作成する
  const authority = 0;

  const [loginIdError, setLoginIdError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [secondPasswordError, setSecondPasswordError] = useState<string>();

  useEffect(() => {
    const pattern = /^[A-Za-z0-9!@#$%^&*()_\+\-={}[\];:'",.<>?/]+$/;

    if (loginId && loginId?.length > 0) {
      setLoginIdError(pattern.test(loginId) ? undefined : "英数字+記号で入力して下さい")
    } else {
      setLoginIdError("必須");
    }

    if (password && password?.length > 0) {
      setPasswordError(pattern.test(password) ? undefined : "英数字+記号で入力して下さい")
    } else {
      setPasswordError("必須");
    }

    if (password !== secondPassword) {
      setSecondPasswordError("パスワードが異なります。");
    } else {
      setSecondPasswordError(undefined);
    }
  }, [loginId, password, secondPassword]);

  const error = Boolean(group_id && name?.trim() && !loginIdError && !passwordError && !secondPasswordError);

  return (
    <Stack width="100%" alignItems="center" pt="70px" gap={4} pb={8}>
      <Stack maxWidth={1200} width="80%" gap="26px">
        <Stack direction="row" alignItems="">
          <Typography fontSize={24} fontWeight="bold">作業者情報</Typography>
        </Stack>

        <TextField
          label="グループ"
          select
          value={group_id}
          onChange={(e) => setGroupId(e.target.value as string)}
          fullWidth
          size="small"
          error={!group_id}
          helperText={!group_id ? "必須" : undefined}
        >
          {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
          ))}
        </TextField>
        <OnChangeTextField label="ユーザー名" value={name ?? ""} onChange={setName} error={!name ? "必須" : undefined} />
        <OnChangeTextField label="ログインID" value={loginId ?? ""} onChange={setLoginId} error={loginIdError} />
        <OnChangeTextField label="パスワード" name="new-password" type="password" autoComplete="new-password" value={password ?? ""} onChange={setPassword} error={passwordError} />
        <OnChangeTextField label="パスワード(確認)" name="new-password" type="password" autoComplete="new-password" value={secondPassword ?? ""} onChange={setSecondPassword} error={secondPasswordError} />
        <StyledButton buttonStyle="primary" disabled={!error} onClick={() => {
          if (group_id && name?.trim() && loginId?.trim() && password?.trim()) {
            pageContext.api.user_data.put({ group_id, name, login_id: loginId, password, authority }).then(() => { 
              update();
              navigate(-1)
            })
          }
        }}>
          <Typography>作成</Typography>
        </StyledButton>
      </Stack>
    </Stack>
  );
}

function UserPage({ pageContext, userData, groups, update }: { pageContext: PageContext, userData: getUserRowType, groups: GroupBase[], update: VoidAction }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = userData?.find(user => user.id === id);

  const [group_id, setGroupId] = useState<string | undefined>(user?.group_id);
  const [name, setName] = useState<string | undefined>(user?.name);
  const [loginId, setLoginId] = useState<string | undefined>(user?.login_id);
  const [password, setPassword] = useState<string | undefined>();
  const [secondPassword, setSecondPassword] = useState<string | undefined>();
  // authorityは編集UIが無いため、現在値をそのまま送って維持する
  const authority = user?.authority ?? 0;

  const [loginIdError, setLoginIdError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [secondPasswordError, setSecondPasswordError] = useState<string>();

  useEffect(() => {
    const pattern = /^[A-Za-z0-9!@#$%^&*()_\+\-={}[\];:'",.<>?/]+$/;

    if (loginId && loginId?.length > 0) {
      setLoginIdError(pattern.test(loginId) ? undefined : "英数字+記号で入力して下さい")
    } else {
      setLoginIdError("必須");
    }

    if (password && password?.length > 0) {
      setPasswordError(pattern.test(password) ? undefined : "英数字+記号で入力して下さい")
    } else {
      setPasswordError(undefined);
    }

    if (password !== secondPassword) {
      setSecondPasswordError("パスワードが異なります。");
    } else {
      setSecondPasswordError(undefined);
    }
  }, [loginId, password, secondPassword]);

  const error = Boolean(group_id && name?.trim() && !loginIdError);
  const noPassError = Boolean(!passwordError || !secondPasswordError);

  if (!user) {
    return <>該当するユーザーがいません。</>;
  }

  return (
    <Stack width="100%" alignItems="center" pt="70px" gap={4} pb={8}>
      <Stack maxWidth={1200} width="80%" gap="26px">
        <Stack direction="row" alignItems="">
          <Typography fontSize={24} fontWeight="bold">作業者情報</Typography>
        </Stack>

        <TextField
          label="グループ"
          select
          value={group_id}
          onChange={(e) => setGroupId(e.target.value as string)}
          fullWidth
          size="small"
          error={!group_id}
          helperText={!group_id ? "必須" : undefined}
        >
          {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
          ))}
        </TextField>
        <OnChangeTextField label="ユーザー名" value={name ?? ""} onChange={setName} error={!name ? "必須" : undefined} />
        <OnChangeTextField label="ログインID" value={loginId ?? ""} onChange={setLoginId} error={loginIdError} />
        <OnChangeTextField label="パスワード" name="new-password" type="password" autoComplete="new-password" value={password ?? ""} onChange={setPassword} error={passwordError} />
        <OnChangeTextField label="パスワード(確認)" name="new-password" type="password" autoComplete="new-password" value={secondPassword ?? ""} onChange={setSecondPassword} error={secondPasswordError} />
        <StyledButton buttonStyle="primary" disabled={!error && noPassError} onClick={async () => {
          if (user) {
            if (password !== undefined && noPassError) {
              console.log("パスワード更新");
              await pageContext.api.user_data.setNewPassword({ id: user.id, password: password });
            }

            if (group_id && name?.trim() && loginId?.trim()) {
              pageContext.api.user_data.patch({ id: user.id, group_id, name, login_id: loginId, authority, }).then(() => { 
                update();
                navigate(-1)
              })
            }
          }
        }}>
          <Typography>更新</Typography>
        </StyledButton>
      </Stack>
    </Stack>
  );
}
